// ACDEV-25945: distinguish a genuine Apple Pay user-cancel from a
// merchant-validation / domain failure. Both reach Adyen's `onError` as the
// SAME `CANCEL` (Apple fires `oncancel` when the user dismisses the sheet AND
// when PassKit rejects the merchant session), so the error payload alone can't
// tell them apart. We capture a signal upstream: Adyen's ApplePay component
// calls the native `ApplePaySession.prototype.completeMerchantValidation(session)`
// with the Apple-signed merchant session. We wrap that once to snapshot the
// session's `domainName` and when it happened, then classify the later CANCEL.
//
// Prefer `ApplePayCancelEvent.sessionError` when present: Apple sets
// `sessionError.code: "unknown"` when PassKit rejects the merchant session
// (sandbox/prod mismatch, bad association, cert, etc.). That is NOT a user
// dismiss — classifying it as user_cancelled hides the real failure.

interface MerchantSessionLike {
  domainName?: string;
}

type ApplePaySessionProto = {
  completeMerchantValidation: (merchantSession: MerchantSessionLike) => void;
};

interface MerchantValidationSnapshot {
  at: number;
  sessionDomain?: string;
  groundTruthDomain: string | null;
  domainMismatch: boolean;
  /** Checkout runs inside a publisher iframe (ACDEV-25945 timing target). */
  isEmbeddedCheckout: boolean;
}

export interface ApplePayCancelEventLike {
  sessionError?: {
    code?: string;
    info?: unknown;
  };
}

let snapshot: MerchantValidationSnapshot | null = null;
let installed = false;

// PassKit validates the merchant session against the top-level browsing
// context. A top-level checkout already has that hostname. Embedded checkout
// prefers ancestorOrigins (available across cross-origin boundaries), then the
// referrer. It must never use the iframe hostname as a substitute.
export const getGroundTruthTopLevelDomain = (
  currentWindow: Pick<Window, 'top' | 'location'> = window,
  currentDocument: Pick<Document, 'referrer'> = document
): string | null => {
  try {
    if (currentWindow.top === currentWindow) {
      return currentWindow.location.hostname || null;
    }
  } catch {
    // Continue with cross-origin-safe sources.
  }

  try {
    const origins = currentWindow.location.ancestorOrigins;
    if (origins && origins.length > 0) {
      return new URL(origins[origins.length - 1]).hostname;
    }
  } catch {
    // ignore malformed origins / unsupported API
  }

  try {
    if (currentDocument.referrer) {
      return new URL(currentDocument.referrer).hostname || null;
    }
  } catch {
    // ignore malformed referrer
  }

  return null;
};

const isEmbeddedCheckoutContext = (currentWindow: Pick<Window, 'top'> = window): boolean => {
  try {
    return currentWindow.top !== currentWindow;
  } catch {
    // Cross-origin iframe: cannot read `top`, but we are embedded.
    return true;
  }
};

// Wrap `completeMerchantValidation` once. Idempotent and safe to call on every
// Apple Pay init; no-ops outside Safari where ApplePaySession is absent.
export const installApplePayMerchantValidationProbe = (
  resolveTopLevelDomain: () => string | null = getGroundTruthTopLevelDomain
): void => {
  if (installed) return;
  const applePaySession = (window as unknown as { ApplePaySession?: { prototype: ApplePaySessionProto } })
    .ApplePaySession;
  const proto = applePaySession?.prototype;
  if (!proto?.completeMerchantValidation) return;
  installed = true;

  const original = proto.completeMerchantValidation;
  proto.completeMerchantValidation = function (this: unknown, merchantSession: MerchantSessionLike) {
    const groundTruthDomain = resolveTopLevelDomain();
    const sessionDomain = merchantSession?.domainName;
    snapshot = {
      at: Date.now(),
      sessionDomain,
      groundTruthDomain,
      domainMismatch: !!sessionDomain && !!groundTruthDomain && sessionDomain !== groundTruthDomain,
      isEmbeddedCheckout: isEmbeddedCheckoutContext(),
    };
    return original.call(this, merchantSession);
  };
};

export type ApplePayCancelClassification =
  | 'domain_mismatch'
  | 'merchant_validation_rejected'
  | 'user_cancelled'
  | 'unknown';

export interface ApplePayCancelDiagnostics {
  classification: ApplePayCancelClassification;
  sessionDomain?: string;
  groundTruthDomain?: string | null;
  msSinceMerchantValidation?: number;
  sessionErrorCode?: string;
}

// Adyen does not expose the native cancel event, so preserve its existing
// timing fallback when PassKit tears down a rejected merchant session — but
// only in embedded checkout. On a top-level checkout page, fast sheet dismiss /
// retry noise dominates and the 800ms window produces false positives.
const PASSKIT_REJECTION_WINDOW_MS = 800;

const isPassKitSessionError = (code?: string): boolean => {
  if (!code) return false;
  // Apple documents "unknown" for merchant-session / environment rejection.
  // Any explicit sessionError code means PassKit aborted — not a silent dismiss.
  return code.length > 0;
};

export const classifyApplePayCancel = (cancelEvent?: ApplePayCancelEventLike | null): ApplePayCancelDiagnostics => {
  const hasExplicitCancelEvent = cancelEvent != null;
  const sessionErrorCode = cancelEvent?.sessionError?.code;
  const passKitError = isPassKitSessionError(sessionErrorCode);

  if (!snapshot) {
    if (passKitError) {
      return { classification: 'merchant_validation_rejected', sessionErrorCode };
    }
    return { classification: 'unknown' };
  }

  const msSinceMerchantValidation = Date.now() - snapshot.at;
  const details = {
    sessionDomain: snapshot.sessionDomain,
    groundTruthDomain: snapshot.groundTruthDomain,
    msSinceMerchantValidation,
    sessionErrorCode,
  };

  if (snapshot.domainMismatch) {
    return { classification: 'domain_mismatch', ...details };
  }

  // Nuvei passes the native cancel event, so prefer its explicit sessionError
  // and do not infer rejection from timing when that event is available.
  if (passKitError) {
    return { classification: 'merchant_validation_rejected', ...details };
  }
  if (
    !hasExplicitCancelEvent &&
    snapshot.isEmbeddedCheckout &&
    msSinceMerchantValidation <= PASSKIT_REJECTION_WINDOW_MS
  ) {
    return { classification: 'merchant_validation_rejected', ...details };
  }

  return { classification: 'user_cancelled', ...details };
};

// Clear the snapshot at the start of each Apple Pay session so a stale value
// from a previous sheet can't misclassify a later cancel.
export const resetApplePayCancelDiagnostics = (): void => {
  snapshot = null;
};

/**
 * TRUE when Apple refused the merchant session for THIS page rather than
 * the player dismissing the sheet — the two ways a domain-verification
 * problem surfaces:
 *   - `domain_mismatch`: we validated a `domainName` that isn't the real
 *     top-level origin PassKit checks against.
 *   - `merchant_validation_rejected`: the domain we sent was right but
 *     Apple has not verified it (also covers cert / merchant-id issues).
 *
 * Neither is fixable inside the embedded frame, which is what the new-tab
 * fallback is for (`shouldUseApplePayTabFallback`). `merchant_validation_rejected`
 * is partly inferred from timing when the PSP hides the native cancel
 * event, so a very fast sheet dismiss can land here; the cost of that
 * false positive is one extra tab, versus repeating a guaranteed failure.
 */
export const isApplePayDomainFailure = (classification?: ApplePayCancelClassification): boolean =>
  classification === 'domain_mismatch' || classification === 'merchant_validation_rejected';
